import { useEffect, useRef } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useTheme } from 'next-themes'
import { useAuth } from '@/api/hooks/useAuth'
import { isStagingMode, isReviewMode, STAGING_ALLOWED_PATHS, REVIEW_ALLOWED_PATHS } from '@/lib/staging'

export function AuthGuard() {
  const { data: user, isLoading, isError } = useAuth()
  const { setTheme } = useTheme()
  const appliedOnce = useRef(false)

  // Apply server theme only once on initial load (not on every refetch)
  useEffect(() => {
    if (user?.theme && !appliedOnce.current) {
      appliedOnce.current = true
      setTheme(user.theme)
    }
  }, [user?.theme, setTheme])

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <img src="/images/logo.png" alt="Blissf" className="mx-auto h-14 w-14 animate-pulse" />
          <p className="mt-3 text-sm text-muted-foreground">Carregando...</p>
        </div>
      </div>
    )
  }

  if (isError || !user) {
    return <Navigate to="/login" replace />
  }

  return <Outlet />
}

export function AdminGuard() {
  const { data: user } = useAuth()

  if (!user?.isAdmin) {
    return <Navigate to="/painel" replace />
  }

  return <Outlet />
}

export function PageGuard() {
  const { data: user } = useAuth()
  const location = useLocation()
  const currentPath = '/' + location.pathname.split('/')[1]

  // Review mode: only allow gerenciador
  if (isReviewMode()) {
    if (!REVIEW_ALLOWED_PATHS.includes(currentPath)) {
      return <Navigate to="/gerenciador" replace />
    }
    return <Outlet />
  }

  // Staging mode: restrict to allowed pages only
  if (isStagingMode() && !STAGING_ALLOWED_PATHS.includes(currentPath)) {
    return <Navigate to="/painel" replace />
  }

  if (user?.allowedPages) {
    if (!user.allowedPages.includes(currentPath)) {
      return <Navigate to="/painel" replace />
    }
  }

  return <Outlet />
}
